// The title the fields save will give the product. The rule is the product
// screen's (CreateProduct's "Generate title" effect), which the server's
// fields PUT also applies (server dashboard/productTitle.js and
// photoPipeline/productFields.js rebuiltTitle): for Rings and Bracelets, a save that
// carries name, metal, color or gemstones rebuilds the title from the saved
// fields, unless it cannot be built whole.
import { capitalizeFirstLetters } from '../../utils'
import { changedFields } from './productFieldsForm'

export const TITLE_FIELDS = ['name', 'metal', 'color', 'gemstones']
// The categories whose title a save rebuilds: the ones publishing is on for
// (server photoPipeline/productFields.js TITLE_CATEGORIES).
const TITLE_CATEGORIES = ['Rings', 'Bracelets']

const collapseSpaces = text => String(text ?? '').replace(/\s+/g, ' ').trim()

function colorWord(colors) {
    switch (colors.length) {
        case 1: return colors[0]
        case 2: return 'Two-Tone'
        case 3: return 'Tri-Tone'
        default: return undefined
    }
}

function singular(category) {
    if (category.toLocaleLowerCase().includes('watches')) return category.slice(0, -2)
    return category.slice(-1) === 's' ? category.slice(0, -1) : category
}

// 'Diamond' | 'Diamond and Ruby' | 'Diamond, CZ and Ruby'
function stonesList(gemstones) {
    const stones = gemstones.map(stone => stone === 'Cubic Zirconia' ? 'CZ' : stone)
    if (stones.length < 2) return stones.join('')
    return `${stones.slice(0, -1).join(', ')} and ${stones[stones.length - 1]}`
}

// Runs of whitespace collapse to one space, as on the product screen, so an
// empty part leaves no double space.
export function buildProductTitle({ category, name, metal, color, gemstones } = {}) {
    const metalText = metal ?? ''
    const colorText = colorWord(color ?? [])
    let material
    if (colorText && metalText) {
        material = metalText.toLowerCase().includes('k') ? `${metalText} ${colorText} Gold` : `${colorText} ${metalText}`
    }
    const stones = stonesList(gemstones ?? [])
    return collapseSpaces(`${material || metalText} ${capitalizeFirstLetters(name ?? '')} ${singular(category ?? '')} ${stones ? `with ${stones}` : ''}`)
}

// `product` is the GET/PUT answer; `draft` and `baseline` the form's input values.
// The stored title when the save would leave it: not a ring or bracelet, no title field
// in the save, a title that already matches (spacing aside), no metal, or a
// stored title field the server could not read (its value null under an id),
// whose words the rebuild would drop.
export function titleAfterSave(product, draft, baseline) {
    if (!TITLE_CATEGORIES.includes(product.category)) return product.title
    const changes = changedFields(draft, baseline)
    if (!TITLE_FIELDS.some(key => Object.hasOwn(changes, key))) return product.title
    const unreadable = key => !Object.hasOwn(changes, key) && product.fields?.[key]?.id && product.fields[key].value === null
    if (TITLE_FIELDS.some(unreadable)) return product.title
    // What the server will hold for each title field once the save lands.
    const value = key => {
        const next = Object.hasOwn(changes, key) ? changes[key] : baseline[key]
        return typeof next === 'string' ? next.trim() || null : next
    }
    if (!value('metal')) return product.title

    const title = buildProductTitle({
        category: product.category,
        name: value('name'),
        metal: value('metal'),
        color: value('color'),
        gemstones: value('gemstones'),
    })
    return title === collapseSpaces(product.title) ? product.title : title
}
